import React from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import ColumnHeader from "../../components/common/ColumnHeader.jsx";
import ReadableCurrency from "../../components/ReadableCurrency.jsx";
import ReadableProjectName from "../../components/ReadableProjectName.jsx";
import SprintChip from "../../components/SprintChip.jsx";

const sortOptions = {
  defaultSort: {
    field: "hours",
    reverse: true,
  },
};

/**
 * Displays a list of projects that a user has worked on, including hours spent
 * and calculated cost.
 *
 * @param {object} props - The component props.
 * @param {object} props.user - The user data containing work history and hourly rate.
 * @returns {React.ReactElement} The rendered component.
 */
function EngineeringUserProjects({
  // Props
  user,
}) {
  const theme = useTheme();

  const projects = React.useMemo(() => {
    const projectsMap = new Map();
    user.userWork?.forEach((work) => {
      if (!work.project) {
        return;
      }
      const projectId = work.project.id;
      if (!projectsMap.has(projectId)) {
        projectsMap.set(projectId, {
          id: projectId,
          name: work.project.name,
          jiraId: work.project.jiraId,
          hours: 0,
          cost: 0,
          sprints: [],
        });
      }
      const projectStats = projectsMap.get(projectId);
      projectStats.hours += Number(work.hours);
      projectStats.cost += Number(work.hours) * Number(user.hourlyRate);

      if (work.sprint && !projectStats.sprints.find((s) => s.id === work.sprint.id)) {
        projectStats.sprints.push(work.sprint);
      }
    });
    const result = Array.from(projectsMap.values());
    result.forEach((project) => {
      project.sprints.sort((a, b) => {
        if (a.endDate && b.endDate) {
          return new Date(b.endDate) - new Date(a.endDate);
        }
        return b.name.localeCompare(a.name);
      });
    });
    return result;
  }, [user.hourlyRate, user.userWork]);

  const [sortedProjects, handleSortSelection, sort] = useSort(projects, sortOptions);

  return (
    <Box sx={{ mx: 2 }}>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="h6">Projects</Typography>
          </Stack>
        </Grid>
        <Grid size={{ xs: "grow", md: 3 }}>
          <ColumnHeader
            id="name"
            display="Project Name"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size="grow" sx={{ display: { xs: "none", md: "block" } }}>
          <ColumnHeader
            id="sprints"
            display="Sprints"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={{ xs: 3.5, md: 1.5 }}>
          <ColumnHeader
            id="hours"
            display="Hours"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={1.5} sx={{ display: { xs: "none", md: "block" } }}>
          <ColumnHeader
            id="cost"
            display="Cost"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
      </Grid>
      <Divider variant="middle" color={theme.palette.primary.dark} sx={{ borderWidth: "1px", mx: -1 }} />
      {sortedProjects.length === 0 && (
        <Box sx={{ alignItems: "center", pt: 2 }}>
          <Typography align="center" sx={{ fontStyle: "italic" }}>
            No project information found
          </Typography>
        </Box>
      )}
      {sortedProjects.map((project) => (
        <React.Fragment key={project.id}>
          <Grid container spacing={1} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: "grow", md: 3 }}>
              <Typography>
                <ReadableProjectName name={project.name} jiraId={project.jiraId} />
              </Typography>
            </Grid>
            <Grid size="grow" sx={{ display: { xs: "none", md: "block" } }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {project.sprints.map((sprint) => (
                  <SprintChip key={sprint.id} sprint={sprint} user={user} />
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 3.5, md: 1.5 }}>
              <Typography>{Math.round(project.hours)}</Typography>
            </Grid>
            <Grid size={1.5} sx={{ display: { xs: "none", md: "block" } }}>
              <ReadableCurrency value={project.cost} />
            </Grid>
          </Grid>
          <Divider variant="middle" sx={{ mx: -1 }} />
        </React.Fragment>
      ))}
    </Box>
  );
}

export default EngineeringUserProjects;
